import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generatePath, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import routing from "routing";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import { isFinishedExam, isMultiAnswersExam } from "src/helpers/examHelpers";
import { usePatchExam } from "src/hooks/useExam";
import { useCreateExam, useRemoveExams } from "src/hooks/useExams";
import ChartIcon from "src/images/Icons/newIcons/chart.svg";
import DeleteIcon from "src/images/Icons/newIcons/delete.svg";
import ChangeNameIcon from "src/images/Icons/newIcons/edit.svg";
import GenerateExamIcon from "src/images/Icons/newIcons/fileClose.svg";
import FolderIcon from "src/images/Icons/newIcons/folder.svg";
import ResumeExamIcon from "src/images/Icons/newIcons/play.svg";
import type { ActionItem } from "./types";

export interface ExamActionRow {
	id: string | number;
	name?: string;
	type?: string;
	status?: string;
	wrongAnswers?: number;
	folderId?: string;
	allResolved?: boolean;
}

interface ExamActionsResult {
	actions: (ActionItem | false)[];
}

interface UseUnfinishedExamActionsParams {
	row: ExamActionRow;
	onWatch?: () => void;
}

export const useUnfinishedExamActions = ({
	row,
	onWatch,
}: UseUnfinishedExamActionsParams): ExamActionsResult => {
	const { openModal } = useModals();
	const { t } = useTranslation();
	const { mutateAsync: generateExam } = useCreateExam() as unknown as {
		mutateAsync: (payload: Record<string, unknown>) => Promise<unknown>;
	};
	const { mutateAsync: removeExams } = useRemoveExams() as unknown as {
		mutateAsync: (ids: (string | number)[]) => Promise<void>;
	};
	const { mutateAsync: renameExam } = usePatchExam() as unknown as {
		mutateAsync: (payload: {
			id: string | number;
			name: string;
		}) => Promise<unknown>;
	};
	const history = useHistory();
	const isStatistycsDisabled =
		isMultiAnswersExam(row.type) && !isFinishedExam(row.status);

	const generateExamFromWrongAnswers = async () => {
		await generateExam({
			unfinishedExamsIds: [{ id: row.id }],
			additionalOption: {
				wrongAnswers: true,
			},
			name: `${t("defaultNames.wrongAnswers")} - ${dayjs(new Date()).format("DD-MM-YYYY")}`,
		});
	};

	return {
		actions: [
			{
				title: t("menuActions.exams.resume"),
				onClick: () => {
					history.push(generatePath(routing.paths.exam, { id: row.id }));
					if (onWatch) {
						return onWatch();
					}
				},
				Icon: <ResumeExamIcon className="icon-small" />,
			},
			{
				title: t("menuActions.exams.changeName"),
				testId: `exam-change-name-${row.id}`,
				onClick: () =>
					openModal(ModalsNames.ChangeNameModal, {
						title: t("modals.changeName.exam.title", "Zmień nazwę egzaminu"),
						label: t("modals.changeName.exam.label", "Nazwa egzaminu"),
						placeholder: t(
							"modals.changeName.exam.placeholder",
							"Wpisz nazwę egzaminu",
						),
						name: row.name,
						onSave: (name: string) => renameExam({ id: row.id, name }),
					}),
				Icon: <ChangeNameIcon className="icon-small" />,
			},
			!isStatistycsDisabled && {
				title: t("menuActions.exams.statistics"),
				onClick: () => {
					openModal(ModalsNames.ExamStatisticsModal, {
						examId: row.id,
					});
				},
				Icon: <ChartIcon className="icon-small" />,
			},
			!!row.wrongAnswers && {
				title: t("menuActions.exams.generateExamFromWrongAnswers"),
				onClick: () => generateExamFromWrongAnswers(),
				Icon: <GenerateExamIcon className="icon-small" />,
			},
			!row.folderId && {
				title: t("menuActions.exams.moveToFolder", "Przenieś do folderu"),
				testId: `exam-move-to-folder-${row.id}`,
				onClick: () => {
					openModal(ModalsNames.MoveToFolderModal, {
						selectedItems: [{ id: row.id, itemId: row.id, itemType: "exam" }],
						isCopyMode: true,
						multiSelect: true,
						custom: {
							title: t("folder.moveToFolderTitle", "Przenieś do folderu"),
							actionLabel: t("folder.move", "Przenieś"),
						},
						onSuccess: () => {
							toast.success(
								t("folder.movedToFolderSuccess", "Przeniesiono do folderu"),
							);
						},
					});
				},
				Icon: <FolderIcon className="icon-small" />,
			},
			{
				title: t("menuActions.exams.remove"),
				className: "list-item-error",
				testId: `exam-delete-${row.id}`,
				onClick: () => {
					openModal(ModalsNames.DeleteItemModal, {
						title: t("modals.deleteItem.exam.title", "Usunąć egzamin?"),
						description: t(
							"modals.deleteItem.exam.description",
							"Egzamin zostanie trwale usunięty razem z odpowiedziami i statystykami. Tej operacji nie można cofnąć.",
						),
						confirmText: t("modals.deleteItem.exam.confirm", "Usuń egzamin"),
						itemLabel: row.name,
						onDelete: () => removeExams([row.id]),
					});
				},
				Icon: <DeleteIcon className="icon-small" />,
			},
		],
	};
};
